import { Platform, Animated } from 'react-native';

/**
 * Shared window style configuration for all contextual side windows
 * Ensures consistent width constraints, animations, and styling across:
 * - HierarchyWindow (Automation Builder)
 * - CanvasWindow (Image Canvas Designer)
 * - EditorWindow (Rich Text Editor)
 */

export interface WindowStyleConfig {
	theme: boolean;
	screenWidth: number;
	widthAnim: Animated.Value;
	windowWidth: number;
	isActive?: boolean; // Optional: if provided, use for simpler opacity control
}

/**
 * Get the shared container styles for contextual side windows
 * All windows use the same constraints: min 800px, max 50% of screen width
 */
export const getWindowContainerStyles = (config: WindowStyleConfig) => {
	const { theme, screenWidth, widthAnim, windowWidth, isActive } = config;

	const baseStyles = {
		backgroundColor: theme ? '#ffffff' : '#1a1a1a',
		borderLeftWidth: 1,
		marginTop: '4dvh',
		borderLeftColor: theme ? '#e5e7eb' : '#374151',
		flexDirection: 'column' as const,
		overflow: 'hidden' as const,
	};

	if (Platform.OS === 'web') {
		// For web, separate static styles from animated styles to avoid CSSStyleDeclaration errors
		// React Native Web has issues when mixing Animated.Value with regular styles in a single object
		const minVisibleWidth = 50; // Start showing content when width is at least 50px
		// Create static styles object explicitly (no spreading to avoid issues)
		const staticStyles: any = {
			backgroundColor: baseStyles.backgroundColor,
			borderLeftWidth: baseStyles.borderLeftWidth,
			marginTop: baseStyles.marginTop,
			borderLeftColor: baseStyles.borderLeftColor,
			flexDirection: baseStyles.flexDirection,
			overflow: baseStyles.overflow,
			// Don't set minWidth here - let the animation value handle it
			// The calculateWindowWidth() function already ensures min 800px when window opens
			maxWidth: screenWidth * 0.5,
		};
		const animatedStyles: any = {
			width: widthAnim,
			// Hide border when width is 0
			// Opacity: fade in as width increases, but ensure it's 1 when width >= minVisibleWidth
			opacity: widthAnim.interpolate({
				inputRange: [0, minVisibleWidth, windowWidth],
				outputRange: [0, 1, 1],
				extrapolate: 'clamp' as const,
			}),
		};
		// Return as array - Animated.View handles style arrays correctly on web
		// Using explicit array to ensure proper handling
		return [staticStyles, animatedStyles] as any;
	} else {
		// Native platform
		const minVisibleWidth = 50;
		return {
			...baseStyles,
			width: widthAnim,
			borderLeftWidth: widthAnim.interpolate({
				inputRange: [0, windowWidth],
				outputRange: [0, 1],
			}) as any,
			opacity: widthAnim.interpolate({
				inputRange: [0, minVisibleWidth, windowWidth],
				outputRange: [0, 1, 1],
				extrapolate: 'clamp' as const,
			}),
		};
	}
};

/**
 * Shared header styles for all window headers
 */
export const getWindowHeaderStyles = (theme: boolean) => ({
	flexDirection: 'row' as const,
	alignItems: 'center' as const,
	justifyContent: 'space-between' as const,
	paddingHorizontal: 16,
	paddingVertical: 12,
	borderBottomWidth: 1,
	borderBottomColor: theme ? '#e5e7eb' : '#374151',
	backgroundColor: theme ? '#f9fafb' : '#1f2937',
});

/**
 * Shared header title styles
 */
export const getWindowHeaderTitleStyles = (theme: boolean) => ({
	fontSize: 16,
	fontWeight: '600' as const,
	color: theme ? '#1f2937' : '#f9fafb',
});

/**
 * Shared close button styles
 */
export const getWindowCloseButtonStyles = () => ({
	padding: 4,
	borderRadius: 4,
});

/**
 * Shared close icon color
 */
export const getWindowCloseIconColor = (theme: boolean) =>
	theme ? '#6b7280' : '#9ca3af';
